import CloseIcon from '@mui/icons-material/Close';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogProps, DialogTitle, IconButton, SxProps, Typography } from '@mui/material';
import { dialogActionsStyles, dialogContentStyles, dialogWrapperStyles, iconStyles } from './TAGDialog.styles';
export interface ITAGDialog extends DialogProps {
  /**
   * Whether the dialog is open or not.
   *
   * @type {boolean}
   * @memberof ITAGDialog
   */
  open: boolean;
  darkScrim?: boolean;
  /**
   * Callback to be called when the dialog is closed.
   *
   * @memberof ITAGDialog
   */
  handleClose: () => void;
  /**
   * Whether the close icon should be shown or not.
   *
   * @type {boolean}
   * @memberof ITAGDialog
   */
  showCloseIcon?: boolean;
  /**
   * Optional styles to be applied to the dialog wrapper.
   *
   * @type {SxProps}
   * @memberof ITAGDialog
   */
  dialogSx?: SxProps;
  /**
   * Optional dialog title. This is a simple string.
   *
   * @type {string}
   * @memberof ITAGDialog
   */
  title?: string;
  /**
   * Optional render function to render the dialog title.
   *
   * @memberof ITAGDialog
   */
  renderTitle?: () => React.ReactNode;
  /**
   * Optional render function to render the simple text as a content of the dialog.
   *
   * @memberof ITAGDialog
   */
  renderTextContent?: () => React.ReactNode;
  /**
   * Optional render function to render more complex dialog content, such as rich text.
   *
   * @memberof ITAGDialog
   */
  renderContent?: () => React.ReactNode;
  /**
   * Optional render function to render the dialog actions.
   *
   * @memberof ITAGDialog
   */
  renderActions?: () => React.ReactNode;
}
export function CloseButton({
  onClose
}: Readonly<{
  onClose: () => void;
}>) {
  return <IconButton aria-label="close" onClick={onClose} sx={iconStyles} data-testid="button_close_dialog" data-sentry-element="IconButton" data-sentry-component="CloseButton" data-sentry-source-file="TAGDialog.tsx">
			<CloseIcon data-sentry-element="CloseIcon" data-sentry-source-file="TAGDialog.tsx" />
		</IconButton>;
}
function TAGDialogTitle({
  title,
  renderTitle
}: Readonly<Pick<ITAGDialog, 'title' | 'renderTitle'>>) {
  if (title) {
    return <DialogTitle data-testid="text_dialog_title">
				<Typography variant="header2">{title}</Typography>
			</DialogTitle>;
  }
  if (renderTitle) {
    return <DialogTitle data-testid="text_dialog_title">{renderTitle()}</DialogTitle>;
  }
  return null;
}
function TAGDialogTextContent({
  renderTextContent
}: Readonly<Pick<ITAGDialog, 'renderTextContent'>>) {
  if (!renderTextContent) return null;
  return <DialogContent sx={dialogContentStyles} data-testid="text_dialog_text_content" data-sentry-element="DialogContent" data-sentry-component="TAGDialogTextContent" data-sentry-source-file="TAGDialog.tsx">
			<DialogContentText data-sentry-element="DialogContentText" data-sentry-source-file="TAGDialog.tsx">{renderTextContent()}</DialogContentText>
		</DialogContent>;
}
function TAGDialogActions({
  renderActions
}: Readonly<Pick<ITAGDialog, 'renderActions'>>) {
  if (!renderActions) return null;
  return <DialogActions sx={dialogActionsStyles} data-testid="group_dialog_actions" data-sentry-element="DialogActions" data-sentry-component="TAGDialogActions" data-sentry-source-file="TAGDialog.tsx">
			{renderActions()}
		</DialogActions>;
}
function TAGDialogContent({
  renderContent
}: Readonly<Pick<ITAGDialog, 'renderContent'>>) {
  if (!renderContent) return null;
  return <DialogContent sx={dialogContentStyles} data-testid="group_dialog_content" data-sentry-element="DialogContent" data-sentry-component="TAGDialogContent" data-sentry-source-file="TAGDialog.tsx">
			{renderContent()}
		</DialogContent>;
}
export default function TAGDialog({
  handleClose,
  open = false,
  darkScrim = false,
  showCloseIcon = false,
  disableScrollLock = false,
  dialogSx,
  title,
  renderTitle,
  renderTextContent,
  renderActions,
  renderContent
}: Readonly<ITAGDialog>) {
  const dialogStyles = {
    ...dialogWrapperStyles,
    ...(darkScrim && {
      '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.8)'
      }
    }),
    ...dialogSx
  };
  return <Dialog open={open} disableScrollLock={disableScrollLock} scroll="paper" onClose={handleClose} sx={dialogStyles} data-testid="section_tag_dialog" data-sentry-element="Dialog" data-sentry-component="TAGDialog" data-sentry-source-file="TAGDialog.tsx">
			{showCloseIcon && <CloseButton onClose={handleClose} />}

			<TAGDialogTitle title={title} renderTitle={renderTitle} data-sentry-element="TAGDialogTitle" data-sentry-source-file="TAGDialog.tsx" />

			<TAGDialogTextContent renderTextContent={renderTextContent} data-sentry-element="TAGDialogTextContent" data-sentry-source-file="TAGDialog.tsx" />

			<TAGDialogContent renderContent={renderContent} data-sentry-element="TAGDialogContent" data-sentry-source-file="TAGDialog.tsx" />

			<TAGDialogActions renderActions={renderActions} data-sentry-element="TAGDialogActions" data-sentry-source-file="TAGDialog.tsx" />
		</Dialog>;
}